import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { createPortal } from "react-dom"
import { X, Move, ExternalLink } from "lucide-react"
import { toast } from "sonner"

import { cn } from "@/lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

// ---------------------------------------------------------------------------
// useDialogDrag — adds drag-by-handle behaviour to a dialog. Pointer events
// originating on inputs / buttons / selects are ignored so the dialog can
// only be dragged by its empty header area.
// ---------------------------------------------------------------------------
// Reads a persisted dialog position from localStorage. `key` is an opaque
// string (usually the dialog's `persistKey` prop, defaulting to "default").
const loadPersistedPos = (key) => {
  try {
    const raw = typeof window !== "undefined" && window.localStorage?.getItem(`dialog-pos:${key}`)
    if (!raw) return null
    const v = JSON.parse(raw)
    if (v && typeof v.x === "number" && typeof v.y === "number") return v
  } catch { /* ignore */ }
  return null
}

const savePersistedPos = (key, pos) => {
  try {
    window.localStorage?.setItem(`dialog-pos:${key}`, JSON.stringify(pos))
  } catch { /* ignore */ }
}

const clearPersistedPos = (key) => {
  try { window.localStorage?.removeItem(`dialog-pos:${key}`) } catch { /* ignore */ }
}

const useDialogDrag = ({ persist, persistKey } = {}) => {
  // Load persisted position synchronously on first mount so the dialog
  // appears in its last location without a flash at centre.
  const [pos, setPos] = React.useState(() => {
    if (persist) {
      const saved = loadPersistedPos(persistKey || "default")
      if (saved) return saved
    }
    return { x: 0, y: 0 }
  })
  const [handleEl, setHandleEl] = React.useState(null)
  const posRef = React.useRef(pos)
  posRef.current = pos
  // Whenever pos changes, persist it (debounced by React itself — state only
  // changes on pointermove).
  React.useEffect(() => {
    if (persist) savePersistedPos(persistKey || "default", pos)
  }, [pos, persist, persistKey])
  React.useEffect(() => {
    const handle = handleEl
    if (!handle) return
    let dragging = false
    let start = { mx: 0, my: 0, x: 0, y: 0 }
    const isInteractive = (el) => {
      return (
        el?.closest &&
        el.closest(
          'input, textarea, select, button, a, [role="combobox"], [role="button"], [contenteditable="true"], [data-no-drag]'
        )
      )
    }
    const onDown = (e) => {
      if (e.button !== 0 && e.pointerType !== "touch") return
      if (isInteractive(e.target)) return
      dragging = true
      start = {
        mx: e.clientX, my: e.clientY,
        x: posRef.current.x, y: posRef.current.y,
      }
      try { handle.setPointerCapture?.(e.pointerId) } catch { /* ignore */ }
      document.body.style.userSelect = "none"
      e.preventDefault()
      e.stopPropagation()
    }
    const onMove = (e) => {
      if (!dragging) return
      setPos({
        x: start.x + (e.clientX - start.mx),
        y: start.y + (e.clientY - start.my),
      })
    }
    const onUp = (e) => {
      if (!dragging) return
      dragging = false
      try { handle.releasePointerCapture?.(e.pointerId) } catch { /* ignore */ }
      document.body.style.userSelect = ""
    }
    handle.addEventListener("pointerdown", onDown)
    handle.addEventListener("pointermove", onMove)
    handle.addEventListener("pointerup", onUp)
    handle.addEventListener("pointercancel", onUp)
    return () => {
      handle.removeEventListener("pointerdown", onDown)
      handle.removeEventListener("pointermove", onMove)
      handle.removeEventListener("pointerup", onUp)
      handle.removeEventListener("pointercancel", onUp)
    }
  }, [handleEl])
  const resetPos = () => {
    setPos({ x: 0, y: 0 })
    if (persist) clearPersistedPos(persistKey || "default")
  }
  return { pos, handleRef: setHandleEl, resetPos }
}

// ---------------------------------------------------------------------------
// PipShell — visual chrome shown around the form inside the Picture-in-Picture
// window so users know how to return to the main app.
// ---------------------------------------------------------------------------
const PipShell = ({ children, onClose }) => (
  <div className="bg-white text-slate-900 min-h-[100vh] p-4">
    <div className="flex items-center justify-between pb-2 mb-3 border-b border-slate-200">
      <span className="text-[10px] font-semibold tracking-widest uppercase text-slate-500">
        Floating window — stays on top of Excel
      </span>
      <button
        type="button"
        onClick={onClose}
        className="text-[11px] font-semibold text-slate-700 px-2.5 py-1 rounded border border-slate-200 bg-slate-50 hover:bg-slate-100 cursor-pointer"
      >
        Return ✕
      </button>
    </div>
    {children}
  </div>
)

// ---------------------------------------------------------------------------
// DialogContent — drop-in replacement for the shadcn DialogContent.
//
// New props (all optional):
//   • draggable:   enable drag-by-header behaviour           (default: true)
//   • popout:      show the "Pop out as floating window" btn  (default: true)
//   • persistPos:  remember last drag position in localStorage (default: true)
//   • persistKey:  localStorage key for persistPos (defaults to "default" —
//                  i.e. all dialogs share one position; override per-dialog
//                  if you want them to track separately).
//
// The pop-out uses the Document Picture-in-Picture API (Chrome/Edge 116+).
// On unsupported browsers a friendly toast nudges the user to drag instead.
// ---------------------------------------------------------------------------
const DialogContent = React.forwardRef(
  ({ className, children, draggable = true, popout = true, persistPos = true, persistKey, ...props }, ref) => {
    const { pos, handleRef, resetPos } = useDialogDrag({ persist: persistPos, persistKey })
    const [pipWindow, setPipWindow] = React.useState(null)
    const [pipMount, setPipMount] = React.useState(null)
    const closeRef = React.useRef(null)
    const isMoved = pos.x !== 0 || pos.y !== 0

    const openPipOut = async () => {
      if (typeof window === "undefined" || !("documentPictureInPicture" in window)) {
        toast.error(
          "Pop-out floating window needs Chrome / Edge 116+. You can still drag this dialog by its top edge."
        )
        return
      }
      try {
        const pip = await window.documentPictureInPicture.requestWindow({
          width: 580, height: 680,
        })
        // ---- 1. Strong fallback stylesheet ----
        // Injected first so the form is *always* readable even if Tailwind /
        // external bundles take a while to load (or fail to load entirely).
        const fallback = pip.document.createElement("style")
        fallback.setAttribute("data-pip-fallback", "1")
        fallback.textContent = `
          html, body { margin: 0; padding: 0; }
          body {
            background: #ffffff; color: #0f172a;
            font: 14px system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
          }
          *, *::before, *::after { box-sizing: border-box; }
          #pip-root { padding: 0; min-height: 100vh; }
          input, textarea, select {
            font: inherit; padding: 6px 10px; border: 1px solid #cbd5e1;
            border-radius: 6px; background: #fff; color: inherit;
            width: 100%; outline: none;
          }
          input:focus, textarea:focus, select:focus {
            border-color: #6366f1; box-shadow: 0 0 0 2px rgba(99,102,241,.15);
          }
          button {
            font: inherit; cursor: pointer; padding: 6px 12px;
            border-radius: 6px; border: 1px solid transparent;
            display: inline-flex; align-items: center; gap: 4px;
          }
          label { font-size: 12px; color: #475569; }
          h1, h2, h3, h4 { color: #0f172a; margin: 0; font-weight: 600; }
          a { color: #2563eb; }
          [hidden] { display: none !important; }
        `
        pip.document.head.appendChild(fallback)

        // ---- 2. Mirror parent document styles ----
        // For every <style> in parent head, clone it (synchronous, no network).
        // For every <link rel="stylesheet">, fetch the content and inline it
        // as <style> — this avoids the async network round-trip that would
        // otherwise leave the window briefly unstyled, and it bypasses any
        // CORS-related blank-stylesheet issues.
        const inlineStylesPromises = []
        document.querySelectorAll('style').forEach((node) => {
          try { pip.document.head.appendChild(node.cloneNode(true)) } catch { /* ignore */ }
        })
        document.querySelectorAll('link[rel="stylesheet"]').forEach((link) => {
          const href = link.href
          if (!href) return
          // Also clone the <link> as a fallback in case fetch fails.
          try { pip.document.head.appendChild(link.cloneNode(true)) } catch { /* ignore */ }
          // And try to inline the same CSS — fetched synchronously w.r.t. the
          // event loop tick we're in. We don't await; CSS rules that arrive
          // late simply restyle the already-rendered DOM.
          inlineStylesPromises.push(
            fetch(href).then((r) => r.ok ? r.text() : "").then((css) => {
              if (!css) return
              const s = pip.document.createElement("style")
              s.setAttribute("data-pip-inlined-href", href)
              s.textContent = css
              pip.document.head.appendChild(s)
            }).catch(() => {})
          )
        })
        // Mirror html/body class names so dark-mode + body bg classes apply.
        pip.document.documentElement.className = document.documentElement.className
        pip.document.body.className = document.body.className

        pip.document.title = "Floating Form"
        const mountEl = pip.document.createElement("div")
        mountEl.id = "pip-root"
        pip.document.body.appendChild(mountEl)
        pip.addEventListener("pagehide", () => {
          setPipMount(null)
          setPipWindow(null)
          // Closing the floating window also closes the underlying dialog —
          // the user is "done" with this form when they close the float.
          closeRef.current?.click()
        })
        setPipWindow(pip)
        setPipMount(mountEl)
        // Fire-and-forget — these resolve in the background and inject
        // additional <style> tags that override the fallback once Tailwind
        // is fully captured.
        Promise.allSettled(inlineStylesPromises)
      } catch (e) {
        toast.error("Couldn't open floating window: " + (e?.message || "unknown error"))
      }
    }

    // Cleanup on unmount: close any open PiP window so it doesn't outlive the
    // host page.
    React.useEffect(() => {
      return () => {
        if (pipWindow) {
          try { pipWindow.close() } catch { /* ignore */ }
        }
      }
    }, [pipWindow])

    // Hide the dialog while popped out, but keep it mounted so closeRef and
    // the children's React tree (incl. Radix context) stay alive.
    const dialogStyle = pipWindow
      ? { display: "none" }
      : draggable
        ? { transform: `translate(calc(-50% + ${pos.x}px), calc(-50% + ${pos.y}px))` }
        : undefined

    return (
      <DialogPortal>
        {!pipWindow && <DialogOverlay />}
        <DialogPrimitive.Content
          ref={ref}
          style={dialogStyle}
          aria-hidden={pipWindow ? true : undefined}
          className={cn(
            "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
            !draggable && "translate-x-[-50%] translate-y-[-50%]",
            className
          )}
          {...props}>
          {/* Close button is always mounted so closeRef.click() works even
              when the dialog is hidden behind the PiP window. */}
          <DialogPrimitive.Close
            ref={closeRef}
            data-no-drag
            className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground z-10">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
          {/* Inline (non-popped-out) chrome */}
          {!pipWindow && draggable && (
            <div
              ref={handleRef}
              className="absolute top-0 left-0 right-0 h-9 cursor-grab active:cursor-grabbing select-none flex items-center px-3 gap-1 rounded-t-lg"
              data-testid="dialog-drag-handle"
              title="Drag this top strip to move the dialog around"
            >
              <Move className="w-3 h-3 text-slate-300 pointer-events-none" />
              {isMoved && (
                <button
                  type="button"
                  data-no-drag
                  onClick={resetPos}
                  title={persistPos
                    ? "Reset to center (also clears the remembered position)"
                    : "Reset to center"}
                  className="ml-1 text-[10px] text-slate-400 hover:text-slate-700 underline decoration-dotted underline-offset-2"
                  data-testid="dialog-reset-pos"
                >
                  reset
                </button>
              )}
            </div>
          )}
          {!pipWindow && popout && (
            <button
              type="button"
              data-no-drag
              onClick={openPipOut}
              title="Pop out as floating window — keeps the form on top of Excel etc. (Chrome/Edge 116+)"
              className="absolute right-12 top-3.5 rounded-sm opacity-60 hover:opacity-100 transition-opacity p-1 hover:bg-slate-100 z-10"
              data-testid="dialog-popout-btn"
            >
              <ExternalLink className="w-4 h-4" />
              <span className="sr-only">Pop out</span>
            </button>
          )}
          {/* Children: render inline normally; portal them into the PiP
              window when popped out. The same React component instances are
              used either way — context, refs, and parent state bindings all
              continue to work because we never re-mount them in a new tree. */}
          {pipWindow && pipMount
            ? createPortal(
                <PipShell onClose={() => pipWindow.close()}>{children}</PipShell>,
                pipMount
              )
            : children}
        </DialogPrimitive.Content>
      </DialogPortal>
    )
  }
)
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
